import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { ScreenLink as Link } from '../permissions/ScreenLink';
import { api } from '../../services/api';

export { MISSING_FINANCIALS_CODE, missingFinancialsEngagementId, missingFinancialsProjectId } from '../../utils/engagementPageModel';

/**
 * P-03/C3 (D-33) — "חסרים נתונים כספיים בהתקשרות — יש להשלים אותם בדף
 * ההתקשרות" + [השלמת נתונים], which opens the engagement's edit form: a
 * button when the page itself can open it (`onComplete`), else a link to the
 * engagement page with `?edit=1`.
 * QA-R2/15 — a project / development (`projectId`): "יש להשלים מחיר ותנאי
 * תשלום בעריכת הפרויקט" + [עריכת הפרויקט] (the project page with `?edit=1`).
 */
export default function MissingFinancialsNotice({ engagementId, projectId = null, onComplete, className = '' }: {
  engagementId: string;
  projectId?: string | null;
  onComplete?: () => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  // D-329 — only the OTE manager and the customer's Account Manager complete
  // the financial data (the server refuses anyone else). Anyone else sees why,
  // and "שליחת בקשה להשלמה" (a notice to them; once a day per engagement).
  const access = useQuery({
    queryKey: ['engagement-financials-access', engagementId],
    queryFn: () => api.getEngagementFinancialsAccess(engagementId).then((r) => r.data),
    enabled: !!engagementId,
  });
  const request = useMutation({
    mutationFn: () => api.requestEngagementFinancials(engagementId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['engagement-financials-access', engagementId] }),
  });
  // QA-D329: nothing while the answer is loading - never "רק מנהל…" by default.
  // QA-D329-2: refused (403 - e.g. a D-106 self-task on a customer the employee has no link
  // to, which still answers 409): the plain explanation, no customer name and no button, so
  // the failed save is never silent.
  if (access.isError) {
    return (
      <div role="status" className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border border-warning-200 bg-warning-50 px-3 py-2.5 text-sm text-warning-800 ${className}`}>
        <p className="flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
          <span>{t('engagementPage.financialsManagersOnlyNoName')}</span>
        </p>
      </div>
    );
  }
  if (!access.isSuccess || !access.data) return null;
  if (access.data.can_complete !== true) {
    const sent = access.data?.request_sent === true || request.isSuccess;
    const customer = access.data?.organization_name;
    return (
      <div role="status" className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border border-warning-200 bg-warning-50 px-3 py-2.5 text-sm text-warning-800 ${className}`}>
        <p className="flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
          <span>{customer ? t('engagementPage.financialsManagersOnly', { customer }) : t('engagementPage.financialsManagersOnlyNoName')}</span>
        </p>
        {sent ? (
          <span className="text-xs font-medium">{t('engagementPage.financialsRequestSent')}</span>
        ) : (
          <button type="button" className="btn btn-secondary btn-sm" disabled={request.isPending} aria-busy={request.isPending || undefined} onClick={() => request.mutate()}>
            {t('engagementPage.requestFinancials')}
          </button>
        )}
      </div>
    );
  }
  if (projectId) {
    return (
      <div role="status" className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border border-warning-200 bg-warning-50 px-3 py-2.5 text-sm text-warning-800 ${className}`}>
        <p className="flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
          <span>{t('engagementPage.missingProjectFinancials')}</span>
        </p>
        {onComplete ? (
          <button type="button" className="btn btn-secondary btn-sm" onClick={onComplete}>{t('engagementPage.editProject')}</button>
        ) : (
          <Link to={`/projects/${projectId}?edit=1`} className="btn btn-secondary btn-sm">{t('engagementPage.editProject')}</Link>
        )}
      </div>
    );
  }
  return (
    <div role="status" className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border border-warning-200 bg-warning-50 px-3 py-2.5 text-sm text-warning-800 ${className}`}>
      <p className="flex items-start gap-2">
        <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
        <span>{t('engagementPage.missingFinancials')}</span>
      </p>
      {onComplete ? (
        <button type="button" className="btn btn-secondary btn-sm" onClick={onComplete}>{t('engagementPage.completeData')}</button>
      ) : (
        <Link to={`/engagements/${engagementId}?edit=1`} className="btn btn-secondary btn-sm">{t('engagementPage.completeData')}</Link>
      )}
    </div>
  );
}
